import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { parseIntegrations, type Integrations } from "./integrations";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export type LeadSearchPanelProps = {
  projectId: string;
  actorId: string;
  storage?: StorageLike | null;
};

type LeadRow = Record<string, unknown>;

const COUNTRIES = ["AT", "DE", "HR", "GB", "FR"] as const;
const LIMITS = ["5", "10"] as const;
const EXAMPLES = ["Mosque Boards", "Mosque", "Islamic Center", "Muslim Association"];
const REFRESH_MS = 2000;
const MAX_JOBS = 10;
const LEARN_PREFIX = "zoho_learn.";
const VIENNA_ALIASES = ["wien", "vienna", "bec"];

const STATUS_LABELS: Record<string, string> = {
  queued: "Čeka izvršenje",
  running: "Pretraga je u tijeku",
  completed: "Gotovo",
  completed_withheld: "Rezultat uskraćen nakon opoziva pristupa",
  cancelled: "Zaustavljeno prije poziva",
  unknown: "Ishod nepoznat",
  rejected: "Pružatelj odbio zahtjev",
};

const HINT_TEXT =
  "Upit je opis tvrtke koju tražite (npr. Mosque Boards), a država je filter sjedišta. " +
  "Grad se ne zadaje u upitu; rezultate po gradu filtrirajte ispod.";

const ERROR_TEXT =
  "Pretraga nije pokrenuta. Provjeri stanje alata i pokušaj ponovno.";

const STARTED_TEXT =
  "Pretraga je pokrenuta. Rezultat se pojavljuje ispod i u Tim → Istraživanje.";

const NO_TOOL_TEXT =
  "Alat nije dopušten. Uključite ga u Postavke → Integrations.";

const NOT_READY_TEXT = "Pozadinski radnik nije spreman.";

const INPUT_CLASS =
  "mt-1 block w-full min-h-11 rounded border border-border bg-background px-3 py-2 text-base";

const LABEL_CLASS = "block text-sm";

const CAPTION_CLASS = "text-xs uppercase tracking-wide text-muted-foreground";

type StoredPrefs = { tool: string; country: string; limit: string };

function defaultStorage(): StorageLike | null {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function isLearnTool(tool: unknown): boolean {
  return typeof tool === "string" && tool.startsWith(LEARN_PREFIX);
}

type ProviderLike = {
  grant?: { enabled?: boolean } | null;
  tools?: unknown;
};

type JobLike = {
  request_id?: unknown;
  tool?: unknown;
  query?: unknown;
  status?: unknown;
  failure?: unknown;
  created_at?: unknown;
  result?: { rows?: unknown } | null;
};

function rawOf(data: Integrations | null): Record<string, unknown> {
  return (data ?? {}) as unknown as Record<string, unknown>;
}

function jobsOf(data: Integrations | null): JobLike[] {
  const jobs = rawOf(data).jobs;
  return Array.isArray(jobs) ? (jobs as JobLike[]) : [];
}

function availableTools(data: Integrations | null): string[] {
  const providers = rawOf(data).providers;
  if (!Array.isArray(providers)) return [];
  const tools: string[] = [];
  for (const provider of providers as ProviderLike[]) {
    if (!provider || !provider.grant || provider.grant.enabled !== true) continue;
    if (!Array.isArray(provider.tools)) continue;
    for (const tool of provider.tools) {
      if (typeof tool !== "string" || isLearnTool(tool)) continue;
      tools.push(tool);
    }
  }
  return tools;
}

function workerReadyOf(data: Integrations | null): boolean {
  return rawOf(data).worker_ready === true;
}

function isPending(job: JobLike): boolean {
  return job.status === "queued" || job.status === "running";
}

function toTimestamp(job: JobLike): number {
  const raw = job.created_at;
  if (typeof raw !== "string") return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function visibleJobs(data: Integrations | null): JobLike[] {
  return jobsOf(data)
    .filter((job) => !isLearnTool(job.tool))
    .sort((a, b) => toTimestamp(b) - toTimestamp(a))
    .slice(0, MAX_JOBS);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function normalizeCity(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .toLowerCase()
    .replace(/đ/g, "d")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function matchesCityFilter(city: unknown, filter: string): boolean {
  const normalized = normalizeCity(city);
  if (!normalized) return false;
  if (VIENNA_ALIASES.includes(filter)) {
    return VIENNA_ALIASES.some((alias) => normalized.includes(alias));
  }
  return normalized.includes(filter);
}

function readPrefs(storage: StorageLike | null, key: string): StoredPrefs | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;
    const { tool, country, limit } = parsed as Record<string, unknown>;
    if (typeof tool !== "string" || typeof country !== "string") return null;
    const limitStr = typeof limit === "number" ? String(limit) : limit;
    if (typeof limitStr !== "string") return null;
    return { tool, country, limit: limitStr };
  } catch {
    return null;
  }
}

function writePrefs(storage: StorageLike | null, key: string, prefs: StoredPrefs): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Pamćenje je najbolji trud; neuspjeh ne smije pokvariti slanje.
  }
}

function pickCountry(preferred: string | null): string {
  if (preferred && (COUNTRIES as readonly string[]).includes(preferred)) {
    return preferred;
  }
  return COUNTRIES[0];
}

function pickLimit(preferred: string | null): string {
  if (preferred && (LIMITS as readonly string[]).includes(preferred)) {
    return preferred;
  }
  return LIMITS[0];
}

function newRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Padamo na rezervni način ispod.
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return "Zahtjev nije uspio.";
}

export function LeadSearchPanel({ projectId, actorId, storage }: LeadSearchPanelProps) {
  const storageRef = useRef<StorageLike | null>(storage === undefined ? defaultStorage() : storage);
  storageRef.current = storage === undefined ? defaultStorage() : storage;

  const identity = `${actorId}:${projectId}`;
  const storageKey = `mb-lead-search:${actorId}:${projectId}`;

  const [data, setData] = useState<Integrations | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const toolTouchedRef = useRef(false);

  const [tool, setTool] = useState("");
  const [country, setCountry] = useState<string>(COUNTRIES[0]);
  const [limit, setLimit] = useState<string>(LIMITS[0]);
  const [query, setQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const tools = useMemo(() => availableTools(data), [data]);
  const toolsKey = tools.join("\u0000");

  // Inicijalizacija obrasca iz pamćenja — jednom po identitetu.
  useEffect(() => {
    const prefs = readPrefs(storageRef.current, storageKey);
    toolTouchedRef.current = false;
    setTool(prefs ? prefs.tool : "");
    setCountry(pickCountry(prefs ? prefs.country : null));
    setLimit(pickLimit(prefs ? prefs.limit : null));
  }, [storageKey]);

  // Dohvat uz stale-zaštitu i lanac osvježavanja dok ima poslova u letu.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const myIdentity = identity;
    const isAlive = () => !cancelled && identity === myIdentity;

    const load = async (): Promise<void> => {
      try {
        const raw = await api.integrations(projectId, controller.signal);
        if (!isAlive()) return;
        const parsed = parseIntegrations(raw, projectId);
        setData(parsed);
        setLoadError(null);
        if (jobsOf(parsed).some(isPending)) {
          timer = setTimeout(() => {
            timer = null;
            if (isAlive()) void load();
          }, REFRESH_MS);
        }
      } catch (error) {
        if (!isAlive() || controller.signal.aborted) return;
        setLoadError(errorMessage(error));
      }
    };

    void load();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
  }, [identity, projectId]);

  // Odabrani alat mora uvijek biti među dostupnima. Dok ga korisnik nije
  // sam promijenio, prednost ima zapamćeni alat, inače prvi dostupni.
  useEffect(() => {
    if (tools.length === 0) return;
    if (tools.includes(tool)) return;
    const prefs = toolTouchedRef.current ? null : readPrefs(storageRef.current, storageKey);
    const stored = prefs && tools.includes(prefs.tool) ? prefs.tool : null;
    setTool(stored ?? tools[0]);
  }, [toolsKey, tool, storageKey]);

  const toolAllowed = tools.includes(tool);
  const trimmedQuery = query.trim();
  const workerReady = workerReadyOf(data);
  const hasData = data !== null;

  const blockedReason = !hasData
    ? null
    : tools.length === 0 || !toolAllowed
      ? NO_TOOL_TEXT
      : !workerReady
        ? NOT_READY_TEXT
        : null;

  const canSubmit =
    !busy &&
    hasData &&
    workerReady &&
    toolAllowed &&
    tools.length > 0 &&
    trimmedQuery.length > 0;

  const pendingRef = useRef<{ id: string; body: string } | null>(null);
  const mutationInFlightRef = useRef(false);
  const mutationControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (mutationControllerRef.current) {
        mutationControllerRef.current.abort();
      }
    };
  }, []);

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit || mutationInFlightRef.current) return;

      const body = {
        tool,
        query: trimmedQuery,
        country,
        limit: Number(limit),
      };
      const serialized = JSON.stringify(body);
      if (!pendingRef.current || pendingRef.current.body !== serialized) {
        pendingRef.current = { id: newRequestId(), body: serialized };
      }
      const requestId = pendingRef.current.id;

      const controller = new AbortController();
      mutationControllerRef.current = controller;
      mutationInFlightRef.current = true;
      setBusy(true);
      setStarted(false);
      setSubmitError(null);

      try {
        const raw = await api.integrationMutation(
          projectId,
          "/research",
          { request_id: requestId, ...body },
          "POST",
          controller.signal,
        );
        const parsed = parseIntegrations(raw, projectId);
        setData(parsed);
        setQuery("");
        pendingRef.current = null;
        setStarted(true);
        writePrefs(storageRef.current, storageKey, { tool, country, limit });
      } catch (error) {
        if (controller.signal.aborted) return;
        setSubmitError(errorMessage(error));
      } finally {
        mutationInFlightRef.current = false;
        mutationControllerRef.current = null;
        setBusy(false);
      }
    },
    [
      canSubmit,
      tool,
      trimmedQuery,
      country,
      limit,
      projectId,
      storageKey,
    ],
  );

  const jobs = useMemo(() => visibleJobs(data), [data]);

  const normalizedFilter = normalizeCity(cityFilter);
  const filterActive = normalizedFilter.length > 0;

  const jobRows = useMemo(() => {
    return jobs.map((job) => {
      const rows = Array.isArray(job.result?.rows) ? (job.result!.rows as LeadRow[]) : [];
      return { job, rows };
    });
  }, [jobs]);

  const totals = useMemo(() => {
    let matched = 0;
    let total = 0;
    for (const { rows } of jobRows) {
      for (const row of rows) {
        total += 1;
        if (!filterActive || matchesCityFilter(row?.city, normalizedFilter)) {
          matched += 1;
        }
      }
    }
    return { matched, total };
  }, [jobRows, filterActive, normalizedFilter]);

  return (
    <section
      className="mx-auto max-w-3xl space-y-4"
      data-testid="lead-search"
      aria-label="Pronađi leadove"
    >
      <h2 className="text-lg font-semibold">Pronađi leadove</h2>

      <form
        className="grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-3"
        data-testid="lead-form"
        onSubmit={onSubmit}
      >
        <label className={LABEL_CLASS}>
          <span className={CAPTION_CLASS}>Alat</span>
          <select
            data-testid="lead-tool"
            className={INPUT_CLASS}
            value={tool}
            onChange={(event) => {
              toolTouchedRef.current = true;
              setTool(event.target.value);
            }}
          >
            {tools.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className={LABEL_CLASS}>
          <span className={CAPTION_CLASS}>Država</span>
          <select
            data-testid="lead-country"
            className={INPUT_CLASS}
            value={country}
            onChange={(event) => setCountry(event.target.value)}
          >
            {COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>

        <label className={LABEL_CLASS}>
          <span className={CAPTION_CLASS}>Broj rezultata</span>
          <select
            data-testid="lead-limit"
            className={INPUT_CLASS}
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
          >
            {LIMITS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <div className="sm:col-span-3">
          <input
            data-testid="lead-query"
            className={INPUT_CLASS}
            type="text"
            maxLength={200}
            value={query}
            placeholder="npr. Mosque Boards"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <button
          data-testid="lead-submit"
          className="sm:col-span-3 min-h-11 rounded-lg bg-primary px-4 py-3 text-sm text-primary-foreground disabled:opacity-50"
          type="submit"
          disabled={!canSubmit}
        >
          Pokreni pretragu
        </button>
      </form>

      {projectId === "prj_minbarlive" ? (
        <div className="lead-examples flex flex-wrap gap-2">
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              data-testid="lead-chip"
              className="min-h-9 rounded-full border border-border px-3 py-1 text-sm"
              onClick={() => setQuery(example)}
            >
              {example}
            </button>
          ))}
        </div>
      ) : null}

      {blockedReason ? (
        <p
          data-testid="lead-blocked"
          className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
        >
          {blockedReason}
        </p>
      ) : null}

      <p data-testid="lead-hint" className="text-xs text-muted-foreground">
        {HINT_TEXT}
      </p>

      {started ? (
        <p
          data-testid="lead-started"
          className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm"
        >
          {STARTED_TEXT}
        </p>
      ) : null}

      {submitError ? (
        <p
          role="alert"
          data-testid="lead-error"
          className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
        >
          {ERROR_TEXT}
        </p>
      ) : null}

      {loadError && !submitError ? (
        <p role="alert" data-testid="lead-load-error">
          Popis alata nije učitan. Provjeri vezu i pokušaj ponovno.
        </p>
      ) : null}

      <label className={LABEL_CLASS}>
        <span className={CAPTION_CLASS}>Filtriraj po gradu</span>
        <input
          data-testid="lead-city-filter"
          className={INPUT_CLASS}
          aria-label="Filtriraj po gradu"
          type="text"
          value={cityFilter}
          onChange={(event) => setCityFilter(event.target.value)}
        />
      </label>

      <p data-testid="lead-claims-note" className="text-xs text-muted-foreground">
        Rezultati su tvrdnje pružatelja, nisu provjereni podaci.
      </p>

      {filterActive ? (
        <p data-testid="lead-filter-count" className="text-xs text-muted-foreground">
          {totals.matched} od {totals.total}
        </p>
      ) : null}

      <div className="lead-jobs space-y-3">
        {jobRows.map(({ job, rows }) => {
          const visibleRows = filterActive
            ? rows.filter((row) => matchesCityFilter(row?.city, normalizedFilter))
            : rows;
          const status = typeof job.status === "string" ? job.status : "unknown";
          const statusLabel = STATUS_LABELS[status] ?? STATUS_LABELS.unknown;
          const showEmptyCity = rows.length > 0 && visibleRows.length === 0;

          return (
            <section
              key={String(job.request_id)}
              data-testid={`lead-job-${job.request_id}`}
              className="rounded-2xl border border-border p-4 space-y-3"
            >
              <header className="flex flex-wrap items-baseline justify-between gap-2">
                <p>
                  <strong>{String(job.query ?? "")}</strong>
                </p>
                <p className="rounded border border-border px-2 py-0.5 text-xs">{statusLabel}</p>
              </header>
              <p className="text-xs text-muted-foreground">{String(job.tool ?? "")}</p>
              {job.failure ? <p>{String(job.failure)}</p> : null}

              {showEmptyCity ? <p>Nema rezultata za taj grad</p> : null}

              {rows.length === 0 &&
              (status === "completed" || status === "completed_withheld") ? (
                <p>Nema rezultata</p>
              ) : null}

              {visibleRows.map((row, rowIndex) => (
                <article
                  key={rowIndex}
                  className="lead-row rounded border border-border p-3 text-sm"
                >
                  <dl className="grid gap-1">
                    {Object.entries(row).map(([key, value]) => (
                      <div
                        key={key}
                        className="lead-row-pair grid grid-cols-[100px_1fr] gap-2 break-words"
                      >
                        <dt className="text-muted-foreground">{key}</dt>
                        <dd>
                          {key === "name" || key === "company_name" ? (
                            <strong>{formatValue(value)}</strong>
                          ) : (
                            formatValue(value)
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </section>
          );
        })}
      </div>
    </section>
  );
}

export default LeadSearchPanel;
